/* ============================================================
   Dathent — Admin Support Hub (/app/admin/support)
   Gated server-side by requireAdmin() (auth-worker proxies to the
   support-worker, which owns support_config + support_conversations +
   support_messages — the Telegram bridge / Discord ingest / tickets,
   migrations 0085/0086). Operator inbox: filter + browse conversations,
   open a thread, reply (forwarded to the bound Telegram chat), classify
   into a ticket (type + assignee + status), and hand-create a ticket that
   never came in through the widget.

   Security is 100% server-side (auth-worker requireAdmin); this screen
   carries no client guard, mirroring AdminBroadcast / AdminActivation /
   AdminPlans. Every read uses cacheTTL:0 (see AdminSupportClient) so the
   inbox always reflects the live D1 state.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, DathentAPIError } from '../services/api/client.js';

const STATUSES = ['open', 'answered', 'ticket', 'closed'];
const TICKET_TYPES = ['bug', 'billing', 'idea', 'other'];
// Filter dropdown only offers Landing/App per spec (bridge-status still
// reports a "contact" route; a contact-sourced conversation is still visible
// under "All" and its source badge renders via adminSupport.source.contact).
const SOURCE_FILTERS = ['landing', 'app'];

const STATUS_COLOR = {
  open: 'var(--accent, #6E5BFF)',
  answered: 'var(--success, #15803D)',
  ticket: 'var(--warning, #B45309)',
  closed: 'var(--text-muted, #9a9aa2)',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function timeAgo(iso, lang) {
  const ts = Date.parse(iso || '');
  if (!Number.isFinite(ts)) return '—';
  const diffSec = Math.round((ts - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return fmtDate(iso);
  try {
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
    if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day');
    return fmtDate(iso);
  } catch {
    return fmtDate(iso);
  }
}

function shortId(id) {
  return String(id || '').replace(/^sc_/, '').slice(0, 8);
}

function errMsg(e) {
  if (e instanceof DathentAPIError) return e.message || 'Request failed';
  return e?.message || 'Request failed';
}

// ─── style primitives (mirrors AdminBroadcast.jsx / AdminPlans.jsx) ────────────

const card = {
  background: 'var(--surface, #16161a)',
  border: '1px solid var(--border, #2a2a30)',
  borderRadius: 14,
  padding: 20,
};
const label = { display: 'block', fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '12px 0 4px' };
const input = {
  width: '100%', boxSizing: 'border-box', background: 'var(--surface-2, #0f0f12)',
  border: '1px solid var(--border, #2a2a30)', borderRadius: 10, color: 'var(--text, #ececf0)',
  padding: '10px 12px', fontSize: 14, fontFamily: 'inherit',
};
const select = { ...input, padding: '8px 10px', fontSize: 13 };
const btn = (disabled) => ({
  background: 'var(--gold, #caa24a)', color: '#0b0b0c', border: 0, borderRadius: 10,
  padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const btnGhost = (disabled) => ({
  background: 'transparent', color: 'var(--text, #ececf0)', border: '1px solid var(--border, #2a2a30)',
  borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const chip = {
  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
  border: '1px solid var(--border, #2a2a30)', borderRadius: 999, padding: '3px 10px',
  color: 'var(--text-muted, #9a9aa2)',
};

function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 13,
      background: '#e0685f18', border: '1px solid #e0685f55', color: '#e8a59f',
    }}>{error}</div>
  );
}

function StatusPill({ status, t }) {
  const color = STATUS_COLOR[status] || STATUS_COLOR.closed;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color,
      border: `1px solid ${color}55`, borderRadius: 999, padding: '2px 10px',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color }} />
      {t(`adminSupport.status.${status}`, status)}
    </span>
  );
}

// ─── Bridge status strip ───────────────────────────────────────────────────────

function BridgeStrip({ bridge, t }) {
  if (!bridge) return null;
  const routeChip = (key, ok) => (
    <span key={key} style={chip}>
      {t(`adminSupport.bridge.route${key[0].toUpperCase()}${key.slice(1)}`, key)}
      <strong style={{ color: ok ? 'var(--success, #15803D)' : 'var(--danger, #B91C1C)' }}>{ok ? '✓' : '✗'}</strong>
    </span>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
      <span style={chip}>
        {t(bridge.bound ? 'adminSupport.bridge.telegramBound' : 'adminSupport.bridge.telegramNotBound', bridge.bound ? 'Telegram bound' : 'Telegram not bound')}
        <strong style={{ color: bridge.bound ? 'var(--success, #15803D)' : 'var(--danger, #B91C1C)' }}>{bridge.bound ? '✓' : '✗'}</strong>
      </span>
      {routeChip('landing', Boolean(bridge.routes?.landing))}
      {routeChip('app', Boolean(bridge.routes?.app))}
      {routeChip('contact', Boolean(bridge.routes?.contact))}
      {bridge.lastError && (
        <span style={{ ...chip, color: '#e8a59f', borderColor: '#e0685f55' }} title={bridge.lastError}>
          {t('adminSupport.bridge.lastError', 'Last error')}: {String(bridge.lastError).slice(0, 40)}{String(bridge.lastError).length > 40 ? '…' : ''}
        </span>
      )}
      {bridge.discordInviteUrl && (
        <a href={bridge.discordInviteUrl} target="_blank" rel="noopener noreferrer" style={{ ...chip, textDecoration: 'none' }}>
          {t('adminSupport.bridge.discordInvite', 'Discord invite')} ↗
        </a>
      )}
    </div>
  );
}

// ─── New ticket modal ───────────────────────────────────────────────────────────

function NewTicketModal({ onClose, onCreated, t }) {
  const [ticketType, setTicketType] = useState('bug');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    if (!message.trim()) {
      setError(t('adminSupport.modal.descriptionRequired', 'Description is required.'));
      return;
    }
    setBusy(true);
    try {
      const { conversation } = await api.adminSupport.create({
        ticketType,
        name: name.trim() || undefined,
        message: message.trim(),
        assignee: assignee.trim() || undefined,
      });
      onCreated(conversation);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={onClose}
    >
      <div style={{ ...card, width: '100%', maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>{t('adminSupport.modal.title', 'New ticket')}</h2>

        <label style={label}>{t('adminSupport.modal.typeLabel', 'Type')}</label>
        <select style={select} value={ticketType} onChange={(e) => setTicketType(e.target.value)}>
          {TICKET_TYPES.map((tt) => (
            <option key={tt} value={tt}>{t(`adminSupport.type.${tt}`, tt)}</option>
          ))}
        </select>

        <label style={label}>{t('adminSupport.modal.nameLabel', 'Title')}</label>
        <input
          style={input} value={name} onChange={(e) => setName(e.target.value)}
          placeholder={t('adminSupport.modal.namePlaceholder', "What's it about?")} maxLength={200}
        />

        <label style={label}>{t('adminSupport.modal.descriptionLabel', 'Description')}</label>
        <textarea
          style={{ ...input, minHeight: 120, resize: 'vertical' }} value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('adminSupport.modal.descriptionPlaceholder', 'Describe the issue…')} maxLength={4000}
        />

        <label style={label}>{t('adminSupport.modal.assigneeLabel', 'Assignee (optional)')}</label>
        <input style={input} value={assignee} onChange={(e) => setAssignee(e.target.value)} maxLength={120} />

        <ErrorNote error={error} />
        <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" style={btnGhost(busy)} disabled={busy} onClick={onClose}>
            {t('adminSupport.modal.cancel', 'Cancel')}
          </button>
          <button type="button" style={btn(busy)} disabled={busy} onClick={submit}>
            {busy ? t('adminSupport.modal.creating', 'Creating…') : t('adminSupport.modal.create', 'Create ticket')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── conversation detail ────────────────────────────────────────────────────────

function ConversationDetail({ id, onChanged, t, lang }) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [ticketType, setTicketType] = useState('');
  const [status, setStatus] = useState('open');
  const [assignee, setAssignee] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.adminSupport.get(id);
      setConversation(res.conversation);
      setMessages(res.messages || []);
      setTicketType(res.conversation?.ticket_type || '');
      setStatus(res.conversation?.status || 'open');
      setAssignee(res.conversation?.assignee || '');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [id, load]);

  const sendReply = async () => {
    const body = replyText.trim();
    if (!body || sending) return;
    setSending(true); setError(null);
    // Optimistic append — the backend also forwards the reply to Telegram
    // (best-effort, non-blocking on that side too).
    const optimistic = {
      id: `optimistic_${Date.now()}`,
      direction: 'out',
      author: 'admin',
      body,
      created_at: new Date().toISOString(),
    };
    setMessages((cur) => [...cur, optimistic]);
    setReplyText('');
    try {
      const { message } = await api.adminSupport.reply(id, body);
      setMessages((cur) => cur.map((m) => (m.id === optimistic.id ? message : m)));
      onChanged?.();
    } catch (e) {
      setMessages((cur) => cur.filter((m) => m.id !== optimistic.id));
      setReplyText(body);
      setError(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  const saveClassification = async () => {
    setSaving(true); setError(null);
    try {
      const { conversation: updated } = await api.adminSupport.classify(id, {
        ticketType: ticketType || null,
        status,
        assignee: assignee.trim() || null,
      });
      setConversation(updated);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={card}>{t('adminSupport.loading', 'Loading…')}</div>;
  if (!conversation) return <div style={card}><ErrorNote error={error || t('adminSupport.notFound', 'Conversation not found.')} /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>
              #{shortId(conversation.id)} · {conversation.name || conversation.email || '—'}
            </h2>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusPill status={conversation.status} t={t} />
              <span style={chip}>{t(`adminSupport.source.${conversation.source}`, conversation.source)}</span>
              {conversation.ticket_type && <span style={chip}>{t(`adminSupport.type.${conversation.ticket_type}`, conversation.ticket_type)}</span>}
              {conversation.email && <span style={{ fontSize: 12, color: 'var(--text-muted, #9a9aa2)' }}>{conversation.email}</span>}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted, #9a9aa2)' }}>
              {t('adminSupport.detail.created', 'Created')} {fmtDate(conversation.created_at)} · {t('adminSupport.detail.updated', 'Updated')} {fmtDate(conversation.updated_at)}
            </div>
          </div>
          <button type="button" style={btnGhost(false)} onClick={load}>{t('adminSupport.refresh', 'Refresh')}</button>
        </div>
      </div>

      {/* Thread */}
      <div style={card}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 420, overflowY: 'auto' }}>
          {messages.length === 0 && (
            <p style={{ color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>{t('adminSupport.empty', 'No conversations yet.')}</p>
          )}
          {messages.map((m) => {
            const out = m.direction === 'out';
            return (
              <div key={m.id} style={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '75%', borderRadius: 12, padding: '10px 12px', fontSize: 14, lineHeight: 1.5,
                  background: out ? 'var(--gold, #caa24a)' : 'var(--surface-2, #0f0f12)',
                  color: out ? '#0b0b0c' : 'var(--text, #d8d8de)',
                  border: out ? 'none' : '1px solid var(--border, #2a2a30)',
                  whiteSpace: 'pre-wrap',
                }}>
                  <div>{m.body}</div>
                  <div style={{ marginTop: 4, fontSize: 10.5, opacity: 0.7 }}>
                    {m.author ? `${m.author} · ` : ''}{fmtDate(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
          <textarea
            style={{ ...input, flex: 1, minHeight: 44, resize: 'vertical' }}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={t('adminSupport.detail.replyPlaceholder', 'Write a reply…')}
            maxLength={4000}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply(); }}
          />
          <button type="button" style={btn(sending || !replyText.trim())} disabled={sending || !replyText.trim()} onClick={sendReply}>
            {sending ? t('adminSupport.detail.sending', 'Sending…') : t('adminSupport.detail.send', 'Send')}
          </button>
        </div>
      </div>

      {/* Classify */}
      <div style={card}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{t('adminSupport.detail.classify', 'Classify')}</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
          <div style={{ flex: '1 1 160px' }}>
            <label style={label}>{t('adminSupport.detail.ticketTypeLabel', 'Ticket type')}</label>
            <select style={select} value={ticketType} onChange={(e) => setTicketType(e.target.value)}>
              <option value="">{t('adminSupport.detail.notATicket', 'Not a ticket')}</option>
              {TICKET_TYPES.map((tt) => (
                <option key={tt} value={tt}>{t(`adminSupport.type.${tt}`, tt)}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={label}>{t('adminSupport.detail.statusLabel', 'Status')}</label>
            <select style={select} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{t(`adminSupport.status.${s}`, s)}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={label}>{t('adminSupport.detail.assigneeLabel', 'Assignee')}</label>
            <input
              style={input} value={assignee} onChange={(e) => setAssignee(e.target.value)}
              placeholder={t('adminSupport.detail.assigneePlaceholder', 'e.g. Viktor')} maxLength={120}
            />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button type="button" style={btn(saving)} disabled={saving} onClick={saveClassification}>
            {saving ? t('adminSupport.detail.saving', 'Saving…') : t('adminSupport.detail.save', 'Save')}
          </button>
        </div>
      </div>

      <ErrorNote error={error} />
    </div>
  );
}

// ─── screen shell ───────────────────────────────────────────────────────────────

export function AdminSupport() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({ status: 'all', type: 'all', source: 'all' });
  const [conversations, setConversations] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.adminSupport.list({
        status: filters.status, ticketType: filters.type, source: filters.source,
      });
      setConversations(res.conversations || []);
      setNextCursor(res.next_cursor || null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.type, filters.source]);

  const loadBridge = useCallback(async () => {
    try {
      const res = await api.adminSupport.bridgeStatus();
      setBridge(res);
    } catch {
      setBridge(null);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadBridge(); }, [loadBridge]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.adminSupport.list({
        status: filters.status, ticketType: filters.type, source: filters.source, cursor: nextCursor,
      });
      setConversations((cur) => [...cur, ...(res.conversations || [])]);
      setNextCursor(res.next_cursor || null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const onCreated = (conversation) => {
    setShowNewTicket(false);
    setConversations((cur) => [conversation, ...cur]);
    setSelectedId(conversation.id);
  };

  const selected = useMemo(() => conversations.find((c) => c.id === selectedId) || null, [conversations, selectedId]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '8px 4px 64px' }}>
      <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--gold, #caa24a)' }}>Admin · internal</div>
          <h1 style={{ margin: '4px 0 2px', fontSize: 26 }}>{t('adminSupport.title', 'Support Hub')}</h1>
          <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)', fontSize: 14 }}>
            {t('adminSupport.subtitle', 'Mini-chat, Telegram bridge and tickets — operator inbox.')}
          </p>
          <BridgeStrip bridge={bridge} t={t} />
        </div>
        <button type="button" style={btn(false)} onClick={() => setShowNewTicket(true)}>
          + {t('adminSupport.newTicket', 'New ticket')}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <select style={{ ...select, width: 'auto' }} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
          <option value="all">{t('adminSupport.filters.statusAll', 'All')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{t(`adminSupport.filters.status${s[0].toUpperCase()}${s.slice(1)}`, s)}</option>
          ))}
        </select>
        <select style={{ ...select, width: 'auto' }} value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}>
          <option value="all">{t('adminSupport.filters.typeAll', 'All types')}</option>
          {TICKET_TYPES.map((tt) => (
            <option key={tt} value={tt}>{t(`adminSupport.filters.type${tt[0].toUpperCase()}${tt.slice(1)}`, tt)}</option>
          ))}
        </select>
        <select style={{ ...select, width: 'auto' }} value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}>
          <option value="all">{t('adminSupport.filters.sourceAll', 'All sources')}</option>
          {SOURCE_FILTERS.map((s) => (
            <option key={s} value={s}>{t(`adminSupport.filters.source${s[0].toUpperCase()}${s.slice(1)}`, s)}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 360px) 1fr', gap: 20, alignItems: 'start' }}>
        {/* Left: list */}
        <div style={card}>
          {loading ? (
            <p style={{ color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>{t('adminSupport.loading', 'Loading…')}</p>
          ) : conversations.length === 0 ? (
            <p style={{ color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>{t('adminSupport.empty', 'No conversations yet.')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {conversations.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedId(c.id)}
                  style={{
                    textAlign: 'left', background: selectedId === c.id ? 'var(--surface-2, #0f0f12)' : 'transparent',
                    border: '1px solid var(--border, #2a2a30)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer',
                    color: 'var(--text, #ececf0)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      #{shortId(c.id)} {c.name || c.email || '—'}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted, #9a9aa2)', whiteSpace: 'nowrap' }}>
                      {timeAgo(c.updated_at, i18n.language)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                    <StatusPill status={c.status} t={t} />
                    <span style={chip}>{t(`adminSupport.source.${c.source}`, c.source)}</span>
                    {c.ticket_type && <span style={chip}>{t(`adminSupport.type.${c.ticket_type}`, c.ticket_type)}</span>}
                  </div>
                  {c.last_message_preview && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted, #9a9aa2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.last_message_preview}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
          {nextCursor && (
            <div style={{ marginTop: 12 }}>
              <button type="button" style={btnGhost(loadingMore)} disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? t('adminSupport.loading', 'Loading…') : t('adminSupport.loadMore', 'Load more')}
              </button>
            </div>
          )}
          <ErrorNote error={error} />
        </div>

        {/* Right: detail */}
        <div>
          {selected ? (
            <ConversationDetail id={selected.id} onChanged={loadList} t={t} lang={i18n.language} />
          ) : (
            <div style={{ ...card, color: 'var(--text-muted, #9a9aa2)' }}>
              {t('adminSupport.selectPrompt', 'Select a conversation on the left, or create a new one.')}
            </div>
          )}
        </div>
      </div>

      {showNewTicket && (
        <NewTicketModal onClose={() => setShowNewTicket(false)} onCreated={onCreated} t={t} />
      )}
    </div>
  );
}

export default AdminSupport;
